import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { Drawer } from 'rsuite';
import moment from 'moment-timezone';
import { isPossiblePhoneNumber } from 'react-phone-number-input';
import RedemptionRenotifyModal from '../../components/Modals/RedemptionRenotifyModal';
import { FiRefreshCw } from 'react-icons/fi';
import {
  Alert,
  Button,
  OverlayTrigger,
  Popover,
  Tooltip,
} from 'react-bootstrap';
import * as FileSaver from 'file-saver';
import Papa from 'papaparse';

import useComponentState from '../../utils/useComponentState';
import {
  Loading,
  PageHeader,
  Pagination,
  EarningItem,
  AppButton,
} from '../../components';
import { isValidateEmail } from '../../utils/validations';
import {
  getEarnings,
  unredeem,
  getEarningsByDate,
  getEarningsByRidRcode,
  getEarningsByRid,
  getAllRedemptionInExcelSheet,
  getAllRedemptionByDateInExcelSheet,
  unredeemState,
  renotifyRedemption,
} from '../../api/earning';
import { login } from '../../api/auth';
import { sortBydate } from './../../components/hoc/SortField';
import SearchByDate from '../../components/SearchByDate/SearchByDate.components';
import SearchByRedemtionIdOrReferanceCode from '../../components/SearchByRedemtionIdOrReferanceCode/SearchByRedemtionIdOrReferanceCode.component';
import SearchBySubMerchant from '../../components/SearchBySubMerchant/SearchBySubMerchant';
import SearchRedemptionDrodown from '../../components/SearchRedemptionDrodown/SearchRedemptionDrodown.component';
import UnredeemPopup from '../../components/unredeemPopup/UnredeemPopup.component';
import { faCopy, faPlus, faMinus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import refreshIcon from '../../styles/images/refresh.png';

import styles from './ViewEarnings.module.scss';
import './PhoneInput.scss';
import { EarningsRoles } from '../../types/RedemptionEnums';
import { copyToClipboard } from '../../utils/helper';
import { APP_PARAMS } from '../../constants/appParams';
import { useGetQueryParams } from '../../hooks/useGetQueryParams';
import { useSetQueryParams } from '../../hooks/useSetQueryParams';
import {
  formatDateTime,
  formatRedemptionAmount,
} from '../../utils/dataFormatter';
import { DATE_TIME_TYPES } from '../../constants/dataTimeTypes';

const KEELLS_INTEGRATION_SUB_MERCHANT_ID = 1126;
const KEELLS_WITHOUT_CODES_SUB_MERCHANT_ID = 988;

/**
 * @component ViewEarnings
 * @description Main component for viewing and managing earnings/redemptions. Provides comprehensive
 * functionality for filtering, viewing details, unredeeeming transactions, and managing notifications.
 *
 * @param {Object} props - Component props
 * @param {Object} props.auth - Authentication state from Redux store
 * @param {Object} props.user - User state from Redux store containing current user and users list
 * @param {string} props.role - Current user's role from Redux store
 *
 * @returns {JSX.Element} The ViewEarnings component
 */
const ViewEarnings = (props) => {
  // Extract query parameters for pagination, filtering, and selection
  const queryParams = useGetQueryParams([
    APP_PARAMS.PAGE,
    APP_PARAMS.START_DATE,
    APP_PARAMS.END_DATE,
    APP_PARAMS.SEARCH_KEY,
    APP_PARAMS.SEARCH_ID,
    APP_PARAMS.SELECTED,
  ]);
  const setQueryParam = useSetQueryParams();

  const merchantId =
    props.auth.auth.details.role === 'ROLE_SUB_MERCHANT_ADMINISTRATOR'
      ? props.user.currentUser?.merchant?.id
      : props.user.currentUser?.id;
  const isMerchantWithCustomLabels =
   // String(merchantId) === '1' ;
     String(merchantId) === '90' ;
    // && props.user.currentUser?.name?.trim().toLowerCase() === 'aitken spence 1';

  const [state, setState] = useComponentState({
    earningsLoading: false,
    authLoading: false,
    earnings: {
      total: 0,
      current: queryParams.page - 1 || 0,
      earnings: [],
    },
    earningsError: null,
    selectedEarning: null,
    unredemptionLoading: false,
    unredemptionError: null,
    startDate: queryParams.startDate
      ? moment(queryParams.startDate).toDate()
      : null,
    endDate: queryParams.endDate ? moment(queryParams.endDate).toDate() : null,
    searchRidIcode: queryParams.searchKey || '',
    selectIdType: queryParams.searchId || 'REDEMPTION_ID',
    selectSubMearchant: isMerchantWithCustomLabels
      ? KEELLS_INTEGRATION_SUB_MERCHANT_ID
      : 0,
    subMerchantManuallySelected: false,
    showUnredeemPopup: false,
    password: '',
    passwordError: '',
    downloadStatus: '',
    downloadErrorMessage: '',
    subMerchants: [],
    mobileNumberFields: [],
    emailFields: [],
    validated: false,
    notifyTo: '',
    recipient: '',
    showRenotifyModal: false,
    overrideNumber: false,
    overrideEmail: false,
    resendSuccess: false,
    resendLoading: false,
    hasError: false,
    copied: false,
    isInitialLoad: true,
  });

  // Extract authentication details from Redux store
  const {
    auth: {
      auth: {
        token,
        details: { role },
      },
    },
  } = props;

  const {
    earningsLoading,
    authLoading,
    earnings: { total, current, earnings },
    earningsError,
    selectedEarning,
    unredemptionLoading,
    unredemptionError,
    startDate,
    endDate,
    searchRidIcode,
    selectSubMearchant,
    subMerchantManuallySelected,
    selectIdType,
    showUnredeemPopup,
    password,
    passwordError,
    downloadStatus,
    downloadErrorMessage,
    subMerchants,
    mobileNumberFields,
    emailFields,
    validated,
    notifyTo,
    recipient,
    showRenotifyModal,
    overrideNumber,
    overrideEmail,
    resendSuccess,
    resendLoading,
    hasError,
    copied,
  } = state;

  const { users, currentUser } = props.user;

  const canViewEarnings =
    role === EarningsRoles.SubMerchantAdmin
      ? currentUser?.merchant?.viewEarningsForSubMerchant === true
      : true;

  /**
   * Effect hook for handling query params and API calls on date/search changes
   * Triggers different API calls based on the filter combination:
   * - Date range only: calls handleGetEarningsSearchByDateFromApi
   * - Search term only or both: calls handleGetEarningsSearchByRidRcodeFromApi
   * - No filters: calls handleGetEarningsFromApi
   *
   * @dependency {Object} props.user.currentUser - Current user object
   * @dependency {Date|null} startDate - Filter start date
   * @dependency {Date|null} endDate - Filter end date
   */
  useEffect(() => {
    const pageToUse = state.isInitialLoad ? queryParams.page : 1;
    if (startDate && endDate && !searchRidIcode) {
      setQueryParam({
        [APP_PARAMS.START_DATE]: startDate,
        [APP_PARAMS.END_DATE]: endDate,
        [APP_PARAMS.SEARCH_ID]: null,
        [APP_PARAMS.SEARCH_KEY]: null,
        [APP_PARAMS.PAGE]: pageToUse,
      });
      handleGetEarningsSearchByDateFromApi(startDate, endDate, pageToUse - 1);
    } else if (
      (!startDate && !endDate && searchRidIcode) ||
      (startDate && endDate && searchRidIcode)
    ) {
      setQueryParam({
        [APP_PARAMS.SEARCH_ID]: selectIdType,
        [APP_PARAMS.SEARCH_KEY]: searchRidIcode,
        [APP_PARAMS.START_DATE]: startDate,
        [APP_PARAMS.END_DATE]: endDate,
        [APP_PARAMS.PAGE]: pageToUse,
      });
      handleGetEarningsSearchByRidRcodeFromApi(searchRidIcode, pageToUse - 1);
    } else if (!startDate && !endDate) {
      setQueryParam({
        [APP_PARAMS.SEARCH_ID]: null,
        [APP_PARAMS.SEARCH_KEY]: null,
        [APP_PARAMS.START_DATE]: null,
        [APP_PARAMS.END_DATE]: null,
        [APP_PARAMS.PAGE]: pageToUse,
      });
      handleGetEarningsFromApi(pageToUse - 1);
    }

    if (state.isInitialLoad) {
      setState({ isInitialLoad: false });
    }

    getSubMerchants();
    //eslint-disable-next-line
  }, [props.user.currentUser, startDate, endDate]);

  const refreshEarnings = () => {
    const defaultSubMerchant = isMerchantWithCustomLabels
      ? KEELLS_INTEGRATION_SUB_MERCHANT_ID
      : 0;
    setState({
      startDate: null,
      endDate: null,
      searchRidIcode: '',
      selectSubMearchant: defaultSubMerchant,
      subMerchantManuallySelected: false,
    });
    setQueryParam({
      [APP_PARAMS.PAGE]: 1,
      [APP_PARAMS.START_DATE]: null,
      [APP_PARAMS.END_DATE]: null,
      [APP_PARAMS.SEARCH_KEY]: null,
      [APP_PARAMS.SEARCH_ID]: null,
    });
    handleGetEarningsFromApi(0, defaultSubMerchant);
    getSubMerchants();
  };

  /**
   * Fetches and sets sub-merchants list for filtering
   * Only executes if current user is not a sub-merchant administrator
   */
  const getSubMerchants = () => {
    if (!checkForSubMerchantAdmin()) {
      const user = users.filter((item) => item.merchant.id === currentUser.id);
      setState({ subMerchants: user[0]?.subMerchants });
    }
  };

  /**
   * Checks if the current user has sub-merchant administrator role
   * @returns {boolean} True if user is a sub-merchant administrator
   */
  const checkForSubMerchantAdmin = () => {
    return role === 'ROLE_SUB_MERCHANT_ADMINISTRATOR';
  };

  /**
   * Handles different error codes from API responses and sets appropriate error messages
   * @param {Object} error - Error object from API response
   */
  const handleErrorCodes = (error) => {
    switch (error.message) {
      case 'User is not a merchant.':
        handleSetError('User is not a merchant');
        break;
      case 'Not Found':
        handleSetError('Entity not found');
        break;
      default:
        handleSetError(error.message);
    }
  };

  /**
   * Sets error state and stops loading
   * @param {Object|string} err - Error object or error message
   */
  const handleSetError = (err) => {
    setState({ earningsLoading: false, earningsError: err.message });
  };

  /**
   * Handles pagination page change and triggers appropriate API call based on current filters
   * @param {number} value - New page number (0-based)
   */
  const handleOnPagechange = (value) => {
    // function to handle page change in pagination
    setQueryParam({
      [APP_PARAMS.PAGE]: value + 1,
    });
    if (startDate && endDate && !searchRidIcode) {
      handleGetEarningsSearchByDateFromApi(startDate, endDate, value);
    } else if (searchRidIcode.trim() !== '') {
      handleGetEarningsSearchByRidRcodeFromApi(searchRidIcode, value);
    } else {
      handleGetEarningsFromApi(value);
    }
  };

  /**
   * Handles search form submission for redemption ID/reference code search
   * Updates query parameters and triggers appropriate API call based on search input
   * @param {Event} data - Form submission event
   */
  const handleOnSubmitSearchRidRcode = (data) => {
    data.preventDefault();
    if (searchRidIcode.trim() !== '') {
      if (startDate && endDate) {
        setQueryParam({
          [APP_PARAMS.START_DATE]: startDate,
          [APP_PARAMS.END_DATE]: endDate,
          [APP_PARAMS.SEARCH_ID]: selectIdType,
          [APP_PARAMS.SEARCH_KEY]: searchRidIcode,
        });
      } else {
        setQueryParam({
          [APP_PARAMS.SEARCH_ID]: selectIdType,
          [APP_PARAMS.SEARCH_KEY]: searchRidIcode,
        });
      }
      handleGetEarningsSearchByRidRcodeFromApi(searchRidIcode, 0);
    } else {
      setQueryParam({
        [APP_PARAMS.SEARCH_ID]: null,
        [APP_PARAMS.SEARCH_KEY]: null,
        [APP_PARAMS.START_DATE]: null,
        [APP_PARAMS.END_DATE]: null,
        [APP_PARAMS.PAGE]: 1,
      });
      setState({ searchRidIcode: '' });
      handleGetEarningsFromApi(0);
    }
  };

  /**
   * Handles input change in the search field for redemption ID/reference code/merchant name
   * @param {Event} data - Input change event
   */
  const handleOnChangeSearchRidRcode = (data) => {
    setState({ searchRidIcode: data.target.value });
  };

  /**
   * Clears search input and resets query parameters
   * If date filters are active, maintains date filtering; otherwise shows all earnings
   */
  const handleOnCleanSearchRidRcode = () => {
    setState({ searchRidIcode: '' });
    if (startDate && endDate) {
      setQueryParam({
        [APP_PARAMS.START_DATE]: startDate,
        [APP_PARAMS.END_DATE]: endDate,
        [APP_PARAMS.SEARCH_ID]: null,
        [APP_PARAMS.SEARCH_KEY]: null,
        [APP_PARAMS.PAGE]: 1,
      });
      handleGetEarningsSearchByDateFromApi(startDate, endDate, 0);
    } else {
      handleGetEarningsFromApi(0);
      setQueryParam({
        [APP_PARAMS.SEARCH_ID]: null,
        [APP_PARAMS.SEARCH_KEY]: null,
        [APP_PARAMS.START_DATE]: null,
        [APP_PARAMS.END_DATE]: null,
        [APP_PARAMS.PAGE]: 1,
      });
    }
  };

  /**
   * Handles selection of search ID type (REDEMPTION_ID, REFERENCE_CODE, MERCHANT_NAME)
   * Resets search results and clears filters when ID type changes
   * @param {string} data - The selected ID type
   */
  const handleOnSelectIdType = (data) => {
    if (queryParams.searchKey) {
      handleGetEarningsFromApi(0);
      setQueryParam({
        [APP_PARAMS.SEARCH_ID]: null,
        [APP_PARAMS.SEARCH_KEY]: null,
      });
    }
    setState({
      selectIdType: data,
      startDate: null,
      endDate: null,
      searchRidIcode: '',
    });
  };

  /**
   * Handles start date change for date range filtering
   * @param {Date} date - The selected start date
   */
  const handleOnStartDatChange = (date) => {
    setState({ startDate: date });
  };

  /**
   * Handles end date change for date range filtering
   * @param {Date} date - The selected end date
   */
  const handleOnEndDatChange = (date) => {
    setState({ endDate: date });
  };

  /**
   * Fetches earnings data filtered by redemption ID, reference code, or merchant name
   * @param {string} searchKey - The search term
   * @param {number} page - Page number for pagination
   * @param {number} [subMerchant=selectSubMearchant] - Sub-merchant ID for filtering
   */
  const handleGetEarningsSearchByRidRcodeFromApi = async (
    searchKey,
    page,
    subMerchant = subMerchantManuallySelected ? selectSubMearchant : 0
  ) => {
    try {
      setState({ earningsLoading: true, earningsError: null });
      const subMerchantId = checkForSubMerchantAdmin()
        ? currentUser.id
        : subMerchant;
      const request = {
        merchantId,
        subMerchantId,
        searchKey: searchKey || queryParams.searchKey,
        selectIdType: queryParams.searchId || selectIdType,
        startDate: startDate
          ? formatDateTime(startDate, DATE_TIME_TYPES.ISO_DATE)
          : null,
        endDate: endDate
          ? formatDateTime(endDate, DATE_TIME_TYPES.ISO_DATE)
          : null,
        page,
      };
      const response = await getEarningsByRidRcode(request, token);

      sortBydate(response.earnings);

      let selected = null;
      if (queryParams.selected) {
        selected = response.earnings.find(
          (earning) => earning.id == queryParams.selected
        );
      }
      setState({
        selectedEarning: selected,
        earningsLoading: false,
        earnings: { ...response },
      });
    } catch (err) {
      handleErrorCodes(err);
    }
  };

  const handleAddField = (type) => {
    let newObj = {};
    if (type === "mobile") {
      newObj = {
        id: !mobileNumberFields.length
          ? 1
          : mobileNumberFields[mobileNumberFields.length - 1].id + 1,
        value: '',
        error: null,
      };
      setState({
        mobileNumberFields: [...mobileNumberFields, newObj],
        validated: false,
      });
    } else {
      newObj = {
        id: !emailFields.length
          ? 1
          : emailFields[emailFields.length - 1].id + 1,
        value: '',
        error: null,
      };
      setState({
        emailFields: [...emailFields, newObj],
        validated: false,
      });
    }
  };

  /**
   * Removes a field by ID
   * @param {number} id - The ID of the field to remove
   * @param {string} type - The type of the field ('mobile' or 'email')
   */
  const handleRemoveField = (id, type) => {
    const newArray =
      type === 'mobile'
        ? mobileNumberFields.filter((item) => item.id !== id)
        : emailFields.filter((item) => item.id !== id);
    setState({
      mobileNumberFields: type === 'mobile' ? newArray : mobileNumberFields,
      emailFields: type === 'email' ? newArray : emailFields,
    });
  };

  /**
   * Fetches earnings data filtered by date range
   * @param {Date} startDate - Start date for filtering
   * @param {Date} endDate - End date for filtering
   * @param {number} page - Page number for pagination
   * @param {number} [subMerchant=selectSubMearchant] - Sub-merchant ID for filtering
   */
  const handleGetEarningsSearchByDateFromApi = async (
    startDate,
    endDate,
    page,
    subMerchant = selectSubMearchant
  ) => {
    try {
      setState({ earningsLoading: true, earningsError: null });
      const subMerchantId = checkForSubMerchantAdmin()
        ? currentUser.id
        : subMerchant;
      const request = {
        page: page,
        size: 10,
        startDate: formatDateTime(startDate, DATE_TIME_TYPES.ISO_DATE),
        endDate: formatDateTime(endDate, DATE_TIME_TYPES.ISO_DATE),
        subMerchantId,
        merchantId,
      };
      const response = await getEarningsByDate(request, token);
      let selected = null;
      if (queryParams.selected) {
        selected = response.earnings.find(
          (earning) => earning.id === queryParams.selected
        );
      }
      sortBydate(response.earnings);
      setState({
        selectedEarning: selected ? { ...selected } : null,
        earningsLoading: false,
        earnings: { ...response },
      });
    } catch (err) {
      handleErrorCodes(err);
    }
  };

  /**
   * Fetches all earnings data without any filters (default view)
   * @param {number} value - Page number for pagination
   * @param {number} [subMerchant=selectSubMearchant] - Sub-merchant ID for filtering
   */
  const handleGetEarningsFromApi = async (
    value,
    subMerchant = selectSubMearchant
  ) => {
    try {
      setState({ earningsLoading: true, earningsError: null });
      const subMerchantId = checkForSubMerchantAdmin()
        ? currentUser.id
        : subMerchant;
      const request = {
        merchantId,
        subMerchantId,
        page: value || 0,
      };
      const response = await getEarnings(request, token);
      sortBydate(response.earnings);
      let selected = null;
      if (queryParams.selected) {
        selected = response.earnings.find(
          (earning) => earning.id === queryParams.selected
        );
      }
      setState({
        selectedEarning: selected ? { ...selected } : null,
        earningsLoading: false,
        earnings: { ...response },
        initialLoading: false,
      });
    } catch (err) {
      handleErrorCodes(err);
    }
  };

  /**
   * Handles sub-merchant selection for filtering
   * Resets other filters and fetches earnings for the selected sub-merchant
   * @param {number} subMearchant - Selected sub-merchant ID
   */
  const handleSubMearchant = (subMearchant) => {
    setState({
      selectSubMearchant: subMearchant,
      subMerchantManuallySelected: true,
      startDate: null,
      endDate: null,
      searchRidIcode: '',
    });
    handleGetEarningsFromApi(0, subMearchant);
  };

  /**
   * Renders loading spinner component
   * @returns {JSX.Element} Loading component
   */
  const renderLoading = () => {
    return (
      <div style={{ paddingTop: '3rem' }}>
        <Loading text='Loading' />;
      </div>
    );
  };

  /**
   * Renders error message component
   * @param {string} err - Error message to display
   * @returns {JSX.Element} Error display component
   */
  const renderError = (err) => {
    return <div className={styles.error__div}>{err}</div>;
  };

  /**
   * Handles deselecting an earning item
   * Clears the selected earning from query params and state
   */
  const handleOnEarningDeselect = () => {
    setQueryParam({
      [APP_PARAMS.SELECTED]: null,
    });
    if (!unredemptionLoading) {
      setState({ selectedEarning: null, unredemptionError: null });
    }
  };

  /**
   * Handles selecting an earning item for detailed view
   * Updates query params and state with selected earning
   * @param {Object} earning - The earning object to select
   */
  const handleOnEarningSelect = (earning) => {
    setQueryParam({
      [APP_PARAMS.SELECTED]: earning.id,
    });
    setState({ selectedEarning: { ...earning } });
  };

  const handleOnUnredeemClick = async () => {
    try {
      setState({
        unredemptionLoading: true,
      });
      const { id } = selectedEarning;
      await unredeemState(id, token);
      setState({
        unredemptionLoading: false,
        showUnredeemPopup: !showUnredeemPopup,
        password: '',
      });
    } catch (error) {
      setState({
        unredemptionLoading: false,
        password: '',
      });
      switch (error.message) {
        case 'UNREDEEM_PERIOD_END':
          setState({
            unredemptionError: 'Unredeem time period is over',
          });
          break;
        default:
          setState({
            unredemptionError: error.message,
          });
      }
    }
  };

  const handleUnredeem = async () => {
    try {
      const { id } = selectedEarning;
      setState({
        unredemptionLoading: true,
        unredemptionError: null,
      });

      const request = {
        redemptionId: id,
        merchantId,
        role,
        page: current,
      };

      const response = await unredeem(request, token);

      // If searchRidIcode is present, fetch only that redemption and show it using getEarningsByRid
      if (searchRidIcode && searchRidIcode.trim() !== '') {
        let param = {
          page: 0,
          redemptionid: searchRidIcode,
          selectIdType,
        };
        const singleRedemptionResp = await getEarningsByRid(
          state.selectedEarning.merchantId,
          token,
          param
        );
        let singleRedemption = null;
        if (
          singleRedemptionResp &&
          singleRedemptionResp.earnings &&
          singleRedemptionResp.earnings.length > 0
        ) {
          singleRedemption = singleRedemptionResp.earnings[0];
        }
        setState({
          selectedEarning: singleRedemption ? { ...singleRedemption } : null,
          earnings: { ...singleRedemptionResp },
          unredemptionLoading: false,
        });
      } else {
        // Default: update as before
        const updatedSelectedRedemption = response.earnings.find(
          (red) => red.id === id
        );
        sortBydate(response.earnings);
        setState({
          selectedEarning: { ...updatedSelectedRedemption },
          earnings: { ...response },
          unredemptionLoading: false,
        });
      }
    } catch (err) {
      switch (err.message) {
        case 'UNAUTHORIZED_USER':
          setState({
            unredemptionLoading: false,
            unredemptionError: 'Unauthorized Error. Please try again',
          });
          break;
        case 'MERCHANT_MISMATCH':
          setState({
            unredemptionLoading: false,
            unredemptionError: 'Invalid request. Please try again',
          });
          break;
        case 'REDEMPTION_ALREADY_UNREDEEMED':
          setState({
            unredemptionLoading: false,
            unredemptionError: 'Redemption already Unredeemed',
          });
          break;
        case 'CORE-MERCHANT_NOT_FOUND':
          setState({
            unredemptionLoading: false,
            unredemptionError: 'Invalid request. Please try again',
          });
          break;
        case 'MERCHANT_FINANCE_DETAILS_NOT_FOUND':
          setState({
            unredemptionLoading: false,
            unredemptionError: 'Invalid request. Please try again',
          });
          break;
        case 'UNREDEEM_PERIOD_END':
          setState({
            unredemptionLoading: false,
            unredemptionError: 'Unredeem time period is over',
          });
          break;
        default:
          setState({
            unredemptionLoading: false,
            unredemptionError: err.message,
          });
      }
    }
  };

  /**
   * Handles the override of the mobile number field
   */
  const handleOverrideNumber = () => {
    if (!overrideNumber) {
      handleAddField("mobile");
    } else {
      setState({ mobileNumberFields: [] });
    }
    setState({ overrideNumber: !overrideNumber, validated: false });
  };

  /**
   * Handles the override of the email field
   */
  const handleOverrideEmail = () => {
    if (!overrideEmail) {
      handleAddField("email");
    } else {
      setState({ emailFields: [] });
    }
    setState({ overrideEmail: !overrideEmail, validated: false });
  };

  const handleOnNotifyCustomer = () => {
    setState({
      showRenotifyModal: true,
      notifyTo: 'NOTIFY_TO_USER',
      recipient: 'customer',
    });
  };

  const handleOnNotifyMerchant = () => {
    setState({
      showRenotifyModal: true,
      notifyTo: 'NOTIFY_TO_MERCHANT',
      recipient: 'merchant',
    });
  };

  /**
   * Closes renotification modal and resets related state
   */
  const handleClose = () => {
    setState({
      showRenotifyModal: false,
      validated: false,
      resendSuccess: false,
      overrideNumber: false,
      overrideEmail: false,
      mobileNumberFields: [],
      emailFields: [],
    });
  };

  const handleRenotifyRedemption = async () => {
    let mobileNumberArray = [];
    let emailArray = [];
    const { isValid } = isAllPhoneFieldsValid();
    const { isEmailValid } = isAllEmailFieldsValid();
    if (!isValid) {
      mobileNumberFields.map((item) => {
        validatePhoneNumber(item.value, item.id);
      });
      setState({ validated: true });
    } else {
      overrideNumber &&
        mobileNumberFields.map((item) => {
          mobileNumberArray.push(item.value);
        });
    }
    if (!isEmailValid) {
      emailFields.map((item) => {
        validateEmail(item.value, item.id);
      });
      setState({ validated: true });
    } else {
      overrideEmail &&
        emailFields.map((item) => {
          emailArray.push(item.value);
        });
    }

    if (!isValid || !isEmailValid) return;

    try {
      const { id } = selectedEarning;
      setState({ resendLoading: true });

      const request = {
        redemptionId: id,
        mobileNumbers: mobileNumberArray,
        emails: emailArray,
        notifyTo,
      };

      await renotifyRedemption(request, token);
      setState({ resendSuccess: true, resendLoading: false });
    } catch (err) {
      setState({ resendLoading: false });
    }
  };

  /**
   * Validates all phone number fields when override is enabled
   * @returns {Object} Object containing validation status
   */
  const isAllPhoneFieldsValid = () => {
    let isValid = true;
    overrideNumber &&
      mobileNumberFields.map((item) => {
        if (!item.value || (item.value && !isPossiblePhoneNumber(item.value))) {
          isValid = false;
        }
      });
    return { isValid };
  };

  /**
   * Validates all email fields when override is enabled
   * @returns {Object} Object containing validation status
   */
  const isAllEmailFieldsValid = () => {
    let isEmailValid = true;
    overrideEmail &&
      emailFields.map((item) => {
        if (!item.value || (item.value && !isValidateEmail(item.value).isValid)) {
          isEmailValid = false;
        }
      });
    return { isEmailValid };
  };

  /**
   * Validates individual phone number field and sets error state
   * @param {string} receiverMobile - Mobile number to validate
   * @param {number} id - Field ID to update
   */
  const validatePhoneNumber = (receiverMobile, id) => {
    setState((prevState) => {
      const updatedFields = prevState.mobileNumberFields.map((item) => {
        if (item.id === id) {
          let mobileError = '';
          if (receiverMobile) {
            if (!isPossiblePhoneNumber(receiverMobile)) {
              mobileError = 'Enter a valid mobile number';
            }
          } else {
            mobileError = "Mobile number can't be empty";
          }
          return { ...item, error: mobileError };
        }
        // Return the original item if the id doesn't match
        return item;
      });
      return { mobileNumberFields: updatedFields };
    });
  };

  /**
   * Validates individual email field and sets error state
   * @param {string} email - Email to validate
   * @param {number} id - Field ID to update
   */
  const validateEmail = (email, id) => {
    setState((prevState) => {
      const updatedFields = prevState.emailFields.map((item) => {
        if (item.id === id) {
          let emailError = '';
          emailError = isValidateEmail(email).error;
          return { ...item, error: emailError };
        }
        return item;
      });
      return { emailFields: updatedFields };
    });
  };

  /**
   * Handles mobile number field value changes
   * @param {string} receiverMobile - New mobile number value
   * @param {number} id - Field ID to update
   */
  const handleMobileChange = (receiverMobile, id) => {
    const updatedValue = mobileNumberFields.map((item) => {
      if (item.id === id) {
        return { ...item, value: receiverMobile }; // Update matched mobile
      }
      return item;
    });

    setState({ mobileNumberFields: updatedValue });
    if (validated) {
      validatePhoneNumber(receiverMobile, id);
    }
  };

  /**
   * Handles email input change
   * @param {string} email - New email value
   * @param {number} id - Field ID to update
   */
  const handleEmailChange = (email, id) => {
    const updatedValue = emailFields.map((item) => {
      if (item.id === id) {
        return { ...item, value: email };
      }
      return item;
    });

    setState({ emailFields: updatedValue });
    if (validated) {
      validateEmail(email, id);
    }
  };

  /**
   * Handles password input change for unredeem confirmation
   * @param {Event} data - Input change event
   */
  const handleOnPasswordChange = (data) => {
    setState({
      password: data.target.value,
    });
  };

  /**
   * Handles password submission for unredeem confirmation
   * Validates password by attempting login, then proceeds with unredeem
   */
  const handleOnPasswordSubmit = async () => {
    setState({
      authLoading: true,
      passwordError: '',
    });
    const authDetails = {
      email: props.auth.auth.details.email,
      password: password,
    };
    try {
      await login(authDetails);
      setState({
        showUnredeemPopup: false,
        authLoading: false,
      });
      handleUnredeem();
    } catch (error) {
      setState({
        passwordError: 'Incorrect password, please try again',
        authLoading: false,
      });
    }
  };

  /**
   * Handles canceling the unredeem operation
   * Closes popup and resets password-related state
   */
  const handleOnUnredeemCansel = () => {
    setState({
      showUnredeemPopup: false,
      password: '',
      passwordError: '',
    });
  };

  /**
   * Copies redemption ID to clipboard and shows feedback
   * @param {string} text - Text to copy to clipboard
   */
  const copyRedemptionId = (text) => {
    copyToClipboard(text);
    setState({
      copied: true,
    });
    setTimeout(() => {
      setState({
        copied: false,
      });
    }, 2000);
  };

  /**
   * Renders the detailed view of selected earning in the drawer
   * Shows all earning details, actions, and handles loading states
   * @returns {JSX.Element} Detailed earning view component
   */
  const renderSelectedEarning = () => {
    const {
      id,
      dateTime,
      referenceCode,
      voucherId,
      redeemedAmount,
      earnedAmount,
      customerName,
      mobileNo,
      terminalId,
      subMerchantId,
      status,
      subMerchantLocation,
      redeemedLocation,
      unRedeemedDateTime,
      platformEarnedAmount,
      merchantId: mid,
      merchantName,
    } = selectedEarning;

    const locationToDisplay = isMerchantWithCustomLabels
      ? redeemedLocation || subMerchantLocation
      : subMerchantLocation;

    return unredemptionLoading ? (
      renderLoading()
    ) : (
      <div className={styles.drawer__div}>
        <div className={styles.drawer__header}>
          <h5 className={styles.redemption__header}>
            {isMerchantWithCustomLabels ? 'Payment Reference Id' : 'Redemption ID'}:- {id.toUpperCase()}
            <OverlayTrigger
              placement={'top'}
              overlay={
                <Tooltip>
                  {copied
                    ? `Copied ${id.toUpperCase()} ✅`
                    : `Click to copy ${id.toUpperCase()}`}
                </Tooltip>
              }
            >
              <Button
                onClick={() => copyRedemptionId(id.toUpperCase())}
                variant='outline-dark'
                size='sm'
                className={styles.copy_icon}
              >
                <FontAwesomeIcon icon={faCopy} />
              </Button>
            </OverlayTrigger>
          </h5>
        </div>
        <div className={styles.drawer__child}>
          <div className={styles.details__horizontal}>
            <div className={styles.detail__group__nested}>
              <span className={styles.detail__header}>Date/Time</span>
              <span className={styles.detail__value}>
                {moment(dateTime)
                  .tz('Asia/Kolkata')
                  .format('DD/MM/YYYY hh:mm A')}{' '}
                (IST)
              </span>
            </div>
            {unRedeemedDateTime && (
              <div className={styles.detail__group__nested}>
                <span className={styles.detail__header}>Un-Redeemed Time</span>
                <span className={styles.detail__value}>
                  {moment(unRedeemedDateTime)
                    .tz('Asia/Kolkata')
                    .format('DD/MM/YYYY hh:mm A')}{' '}
                  (IST)
                </span>
              </div>
            )}
          </div>
          <div className={styles.details__horizontal}>
            <div className={styles.detail__group__nested}>
              <span className={styles.detail__header}>Voucher ID</span>
              <span className={styles.detail__value}>{voucherId}</span>
            </div>
          </div>
          <div className={styles.details__horizontal}>
            <div className={styles.detail__group__nested}>
              <span className={styles.detail__header}>
                {isMerchantWithCustomLabels ? 'Location' : 'Sub Merchant Location'} ({' '}
                {subMerchantId} )
              </span>
              <span className={styles.detail__value}>
                {locationToDisplay}
              </span>
            </div>
            <div className={styles.detail__group__nested}>
              <span className={styles.detail__header}>Terminal ID</span>
              <span className={styles.detail__value}>{terminalId}</span>
            </div>
          </div>
          <div className={styles.details__horizontal}>
            <div className={styles.detail__group__nested}>
              <span className={styles.detail__header}>
                {isMerchantWithCustomLabels ? 'Order Id' : 'Reference Code'}
              </span>
              <span className={styles.detail__value}>{referenceCode}</span>
            </div>
            <div className={styles.detail__group__nested}>
              <span className={styles.detail__header}>Status</span>
              <span className={styles.detail__value}>{status}</span>
            </div>
          </div>
          <div className={styles.details__horizontal}>
            <div className={styles.detail__group__nested}>
              <span className={styles.detail__header}>Customer Name</span>
              <span className={styles.detail__value}>{customerName}</span>
            </div>
            <div className={styles.detail__group__nested}>
              <span className={styles.detail__header}>Mobile No</span>
              <span className={styles.detail__value}>{mobileNo}</span>
            </div>
          </div>
          <div className={styles.details__horizontal}>
            <div className={styles.detail__group__nested}>
              <span className={styles.detail__header}>
                {isMerchantWithCustomLabels
                  ? 'Redeem Point Amount'
                  : 'Redeemed Amount (LKR)'}
              </span>
              <span className={styles.detail__value}>
                {formatRedemptionAmount(redeemedAmount)}
              </span>
            </div>
            {!isMerchantWithCustomLabels &&
              role !== EarningsRoles.MerchantRedemptions &&
              canViewEarnings && (
                <div className={styles.detail__group__nested}>
                  <span className={styles.detail__header}>
                    Earned Amount (LKR)
                  </span>
                  <span className={styles.detail__value}>
                    {formatRedemptionAmount(earnedAmount)}
                  </span>
                </div>
              )}
          </div>

          {!merchantId && role !== EarningsRoles.MerchantRedemptions && (
            <div className={styles.details__horizontal}>
              <div className={styles.detail__group__nested}>
                <span className={styles.detail__header}>
                  Platform Earned Amount (LKR)
                </span>
                <span className={styles.detail__value}>
                  {formatRedemptionAmount(platformEarnedAmount)}
                </span>
              </div>
              <div className={styles.detail__group__nested}>
                <span
                  className={styles.detail__header}
                >{`Merchant (${mid})`}</span>
                <span className={styles.detail__value}>{merchantName}</span>
              </div>
            </div>
          )}

          {status !== 'UNREDEEMED' && (
            <>
              {(role === 'ROLE_THYAGA_ADMINISTRATOR' ||
                role === 'ROLE_THYAGA_CUSTOMER_CARE') &&
                role !== EarningsRoles.SubMerchantAdmin && (
                <div style={{ display: 'flex' }}>
                  <div style={{ flex: 1 }}>
                    <AppButton
                      text='Renotify Customer'
                      onClick={handleOnNotifyCustomer}
                      type='button'
                      loading={false}
                      containerStyle={{ marginTop: '10px' }}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <AppButton
                      text='Renotify Merchant'
                      onClick={handleOnNotifyMerchant}
                      type='button'
                      loading={false}
                      containerStyle={{ marginTop: '10px' }}
                    />
                  </div>
                </div>
              )}
              <AppButton
                text='Unredeem'
                onClick={handleOnUnredeemClick}
                type='button'
                loading={false}
                containerStyle={{ marginTop: '10px' }}
              />
            </>
          )}
          <UnredeemPopup
            show={showUnredeemPopup}
            onChangeValue={handleOnPasswordChange}
            onSubmit={handleOnPasswordSubmit}
            onCancel={handleOnUnredeemCansel}
            password={password}
            loading={authLoading}
            error={passwordError}
          />
        </div>
        <div>{unredemptionError && renderError(unredemptionError)}</div>
      </div>
    );
  };
  /**
   * Renders appropriate error message when no redemptions are found
   * @returns {JSX.Element} No redemptions message based on active filters
   */
  const renderNoRedemtionError = () => {
    if (startDate && endDate) {
      return (
        <p>
          No redemptions for <b>{formatDateTime(startDate, 'DD/MM/YYYY')}</b> to{' '}
          <b>{formatDateTime(endDate, 'DD/MM/YYYY')}</b>
        </p>
      );
    } else if (searchRidIcode.trim() !== '') {
      return (
        <p>
          No {selectIdType === 'REFERENCE_CODE' ? 'reference' : 'redemptions'}{' '}
          for <b>{searchRidIcode}</b>
        </p>
      );
    } else if (Number(selectSubMearchant) !== 0) {
      const subMerchantDetails = subMerchants.find(
        (subMerchant) => String(subMerchant.id) === String(selectSubMearchant)
      );
      if (!subMerchantDetails) {
        return <p>No redemptions</p>;
      }
      return (
        <p>
          No redemptions for{' '}
          <b>
            {subMerchantDetails.location} ( {subMerchantDetails.id} ){' '}
          </b>
        </p>
      );
    } else {
      return <p>No redemptions</p>;
    }
  };

  /**
   * Determines which Excel download function to use based on user role
   */
  const handleExcelSheetDownload = () => {
    if (role === 'ROLE_THYAGA_ADMINISTRATOR') {
      downloadExcelSheetForAdmin();
    } else {
      downloadExcelSheetForMerchant();
    }
  };

  /**
   * Downloads Excel sheet for admin users with all redemption data
   * Includes platform earning information and merchant details
   */
  const downloadExcelSheetForAdmin = async () => {
    try {
      setState({ downloadStatus: 'DOWNLOADING' });
      let response;
      const request = {
        page: 0,
        subMerchantId: 0,
      };

      if (state.searchRidIcode) {
        request.searchKey = state.searchRidIcode;
        request.selectIdType = state.selectIdType;
        response = await getEarningsByRidRcode(request, token);
      } else {
        if (startDate && endDate) {
          request.startDate = formatDateTime(
            startDate,
            DATE_TIME_TYPES.ISO_DATE
          );
          request.endDate = formatDateTime(endDate, DATE_TIME_TYPES.ISO_DATE);
        }
        response = await getEarningsByDate(request, token);
      }

      const excelDate = response.earnings.map((redemption) => {
        const redemptionDetails = {
          'Redemption Id': redemption.id,
          'Customer Name': redemption.customerName,
          Merchant: redemption.merchantName,
          'Merchant Id': redemption.merchantId,
          'Sub Merchant Id': redemption.subMerchantId,
          'Sub Merchant Name': redemption.subMerchantLocation,
          'Reference Code': redemption.referenceCode,
          'Promo Id': redemption.promoId,
          Amount: redemption.redeemedAmount,
          'Merchant Earning': redemption.earnedAmount,
          'Platform Earning': redemption.platformEarnedAmount,
          'Payable Status': redemption.payableStatus,
          Status: redemption.status,
          'Bank Name': redemption.bankName,
          'Bank Branch Code': redemption.bankBranchCode,
          'Date/Time (IST)': moment(redemption.dateTime)
            .tz('Asia/Kolkata')
            .format('DD/MM/YYYY hh:mm A'),
        };

        return redemptionDetails;
      });
      exportToCSV(excelDate);
    } catch (error) {
      setState({ downloadStatus: 'ERROR' });
    }
    setState({ downloadStatus: '' });
  };

  /**
   * Downloads Excel sheet for merchant users with filtered data based on permissions
   * Handles different filter combinations and sub-merchant restrictions
   */
  const downloadExcelSheetForMerchant = async () => {
    setState({ downloadStatus: 'DOWNLOADING' });
    try {
      let response;
      if (Number(selectSubMearchant) === 0) {
        if (startDate && endDate) {
          response = await getAllRedemptionByDateInExcelSheet(
            token,
            formatDateTime(startDate, DATE_TIME_TYPES.ISO_DATE),
            formatDateTime(endDate, DATE_TIME_TYPES.ISO_DATE),
            checkForSubMerchantAdmin() ? currentUser.id : merchantId
          );
        } else {
          const searchType = state.searchRidIcode ? state.selectIdType : '';
          const searchKey = state.searchRidIcode;
          const id = checkForSubMerchantAdmin() ? currentUser.id : merchantId;
          const backendSearchIdType =
            searchType === 'REDEMPTION_ID' || searchType === 'REFERENCE_CODE'
              ? searchType
              : null;
          const tempResponse = await getAllRedemptionInExcelSheet(
            id,
            token,
            null,
            backendSearchIdType ? searchKey : null,
            backendSearchIdType
          );
          if (searchType === 'REDEMPTION_ID') {
            response = tempResponse.filter(({ id }) =>
              id.toString().includes(searchKey)
            );
          } else if (searchType === 'REFERENCE_CODE') {
            response = tempResponse.filter(({ referenceCode }) =>
              referenceCode.toLowerCase().includes(searchKey.toLowerCase())
            );
          } else if (searchType === 'MERCHANT_NAME') {
            response = tempResponse.filter(({ merchantName }) =>
              merchantName.toLowerCase().includes(searchKey.toLowerCase())
            );
          } else {
            response = tempResponse;
          }
        }
      } else {
        const selectSubMerchantForDownload = selectSubMearchant;
        if (startDate && endDate) {
          const tempResponse = await getAllRedemptionByDateInExcelSheet(
            token,
            formatDateTime(startDate, DATE_TIME_TYPES.ISO_DATE),
            formatDateTime(endDate, DATE_TIME_TYPES.ISO_DATE),
            checkForSubMerchantAdmin() ? currentUser.id : merchantId,
            selectSubMerchantForDownload
          );
          response = tempResponse.filter(
            ({ subMerchantId }) =>
              String(subMerchantId) === String(selectSubMerchantForDownload)
          );
        } else {
          const searchType = state.searchRidIcode ? state.selectIdType : '';
          const searchKey = state.searchRidIcode;
          const id = checkForSubMerchantAdmin() ? currentUser.id : merchantId;
          const backendSearchIdType =
            searchType === 'REDEMPTION_ID' || searchType === 'REFERENCE_CODE'
              ? searchType
              : null;
          const tempResponse = await getAllRedemptionInExcelSheet(
            id,
            token,
            backendSearchIdType ? null : selectSubMerchantForDownload,
            backendSearchIdType ? searchKey : null,
            backendSearchIdType
          );
          if (searchType === 'REDEMPTION_ID') {
            response = tempResponse.filter(({ id }) =>
              id.toString().includes(searchKey)
            );
          } else if (searchType === 'REFERENCE_CODE') {
            response = tempResponse.filter(({ referenceCode }) =>
              referenceCode.toLowerCase().includes(searchKey.toLowerCase())
            );
          } else {
            response = tempResponse.filter(
              ({ subMerchantId }) =>
                String(subMerchantId) === String(selectSubMerchantForDownload)
            );
          }
        }
      }

      const excelDate = response.map((redemption) => {
        const redemptionDetails = isMerchantWithCustomLabels
          ? {
              'Payment Reference Id': redemption.id,
              'Customer Name': redemption.customerName,
              'Sub Merchant Id': redemption.subMerchantId,
              Location: redemption.location || redemption.subMerchantName,
              'Order Id': redemption.referenceCode,
              'Promo Id': redemption.promoId,
              'Redeem Point Amount': redemption.amount,
              'Payable Status': redemption.payableStatus,
              Status: redemption.status,
              'Date/Time (IST)': moment(redemption.timestamp)
                .tz('Asia/Kolkata')
                .format('DD/MM/YYYY hh:mm A'),
            }
          : {
              'Redemption Id': redemption.id,
              'Customer Name': redemption.customerName,
              'Sub Merchant Id': redemption.subMerchantId,
              'Sub Merchant Name': redemption.subMerchantName,
              'Reference Code': redemption.referenceCode,
              'Promo Id': redemption.promoId,
              Amount: redemption.amount,
              'Merchant Earning': redemption.merchantEarning,
              'Payable Status': redemption.payableStatus,
              Status: redemption.status,
              'Date/Time (IST)': moment(redemption.timestamp)
                .tz('Asia/Kolkata')
                .format('DD/MM/YYYY hh:mm A'),
            };
        return redemptionDetails;
      });
      exportToCSV(excelDate);
    } catch (error) {
      setState({ downloadStatus: 'ERROR' });
    }
    setState({ downloadStatus: '' });
  };

  /**
   * Exports data to CSV format using Papa Parse
   * @param {Array} csvData - Array of data objects to export
   */
  const exportToCSV = (csvData) => {
    const csv = Papa.unparse(csvData);
    const data = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    FileSaver.saveAs(data, 'Redemptions.csv');
  };

  /**
   * Renders the earnings list with pagination
   * Shows appropriate message when no earnings are found
   * @returns {JSX.Element} Earnings list component
   */
  const renderEarnings = () => {
    const earningsToRender = earnings.map((earning) => {
      return (
        <EarningItem
          earning={earning}
          role={props.role}
          onClick={handleOnEarningSelect}
          isMerchantWithCustomLabels={isMerchantWithCustomLabels}
          canViewEarnings={canViewEarnings}
          showFullPrecision
        />
      );
    });
    return (
      <div className={styles.offers__list}>
        {earnings.length === 0 ? (
          <div className={styles.redemption_error}>
            {renderNoRedemtionError()}
          </div>
        ) : (
          <div>{earningsToRender}</div>
        )}
        <Drawer
          show={selectedEarning && !showRenotifyModal}
          onHide={handleOnEarningDeselect}
        >
          {selectedEarning && renderSelectedEarning()}
        </Drawer>
      </div>
    );
  };

  /**
   * Renders the page header with search filters and controls
   * Includes date picker, search input, sub-merchant selector, and download button
   * @returns {JSX.Element} Header component with filters
   */
  const renderHeader = () => {
    return (
      <div>
        <PageHeader text='Redemptions' />

        <div className={styles.search_area}>
          <div className={styles.filter_row}>
            <div className={styles.filter_date}>
              <SearchByDate
                onStartDateChange={handleOnStartDatChange}
                onEndDateChange={handleOnEndDatChange}
                startDate={startDate}
                endDate={endDate}
              />
            </div>
            <div className={styles.referenceCode_search}>
              <SearchRedemptionDrodown
                onSelectChange={handleOnSelectIdType}
                value={selectIdType}
                isMerchantWithCustomLabels={isMerchantWithCustomLabels}
              />
              <SearchByRedemtionIdOrReferanceCode
                onSubmit={handleOnSubmitSearchRidRcode}
                onChange={handleOnChangeSearchRidRcode}
                onClean={handleOnCleanSearchRidRcode}
                value={searchRidIcode}
                placeholder={
                  selectIdType === 'REDEMPTION_ID'
                    ? isMerchantWithCustomLabels
                      ? 'Payment Reference Id'
                      : 'Redemption ID'
                    : selectIdType === 'REFERENCE_CODE'
                      ? isMerchantWithCustomLabels
                        ? 'Order Id'
                        : 'Reference Code'
                      : 'Merchant Name'
                }
                searchKey={queryParams.searchKey}
              />
            </div>

            {!checkForSubMerchantAdmin() && (
              <div className={styles.filter_submerchant}>
                <SearchBySubMerchant
                  subMerchants={
                    isMerchantWithCustomLabels
                      ? subMerchants?.filter(
                          (subMerchant) =>
                           // subMerchant.id !== KEELLS_WITHOUT_CODES_SUB_MERCHANT_ID
                          String(subMerchant.id) !==
                            String(KEELLS_WITHOUT_CODES_SUB_MERCHANT_ID)
                        )
                      : subMerchants
                  }
                  onSelect={handleSubMearchant}
                  value={selectSubMearchant}
                  hideAllOption={isMerchantWithCustomLabels}
                />
              </div>
            )}

            <div className={styles.download_button}>
              <Button size='sm' onClick={handleExcelSheetDownload}>
                {(startDate && endDate) ||
                  searchRidIcode ||
                  Number(selectSubMearchant) !== 0
                  ? 'Download'
                  : 'Download All'}
              </Button>
              {downloadStatus === 'DOWNLOADING' && (
                <p>Your report will be downloaded shortly</p>
              )}
              {downloadStatus === 'ERROR' && (
                <p className={styles.download_error_message}>
                  Oops, something went wrong. Please try again
                </p>
              )}
            </div>
            <Button
              className={styles.refresh_button}
              onClick={refreshEarnings}
              variant='outline-dark'
              title='Refresh'
            >
              <FiRefreshCw />
            </Button>
          </div>
        </div>
      </div>
    );
  };

  /**
   * Renders the column header row of the redemptions list
   * @returns {JSX.Element} Table header row
   */
  const renderTableHeader = () => {
    return (
      <div>
        <div className={styles.table__header}>
          <span>Date (IST)</span>
          <span>{isMerchantWithCustomLabels ? 'Payment Reference Id' : 'Redemption Id'}</span>
          {props.role === 'ROLE_THYAGA_ADMINISTRATOR' ? (
            <span>Merchant</span>
          ) : null}
          <span>{isMerchantWithCustomLabels ? 'Location' : 'Sub Merchant'}</span>
          <span>Status</span>
          <span className={styles.amount__header}>
            {isMerchantWithCustomLabels ? 'Redeem Point Amount' : 'Redeemed (LKR)'}
          </span>
          {!isMerchantWithCustomLabels && canViewEarnings && (
            <span className={styles.amount__header}>Earned (LKR)</span>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      <div className={styles.root__div}>
        {renderHeader()}
        <div className={styles.table_scroll}>
          <div className={styles.table_inner}>
            {renderTableHeader()}
            {earningsLoading
              ? renderLoading()
              : earningsError
                ? renderError(earningsError)
                : renderEarnings()}
          </div>
        </div>
        {!earningsLoading &&
          !earningsError &&
          earnings.length > 0 &&
          !(earnings.length < 10 && total === 1) && (
            <Pagination
              pageCount={total}
              onChange={handleOnPagechange}
              currentPage={current}
            />
          )}
        {selectedEarning && (
          <RedemptionRenotifyModal
            show={showRenotifyModal}
            handleClose={handleClose}
            handleRenotifyRedemption={handleRenotifyRedemption}
            overrideNumber={overrideNumber}
            overrideEmail={overrideEmail}
            recipient={recipient}
            handleOverrideNumber={handleOverrideNumber}
            handleOverrideEmail={handleOverrideEmail}
            handleAddField={handleAddField}
            handleRemoveField={handleRemoveField}
            mobileNumberFields={mobileNumberFields}
            emailFields={emailFields}
            handleMobileChange={handleMobileChange}
            handleEmailChange={handleEmailChange}
            resendSuccess={resendSuccess}
            validated={validated}
            selectedEarning={selectedEarning}
            resendLoading={resendLoading}
          />
        )}
      </div>
    </>
  );
};

/**
 * Maps Redux state to component props
 * @param {Object} state - Redux store state
 * @returns {Object} Props object containing auth, user, and role data
 */
const mapStateToProps = (state) => {
  return {
    auth: state.auth,
    user: state.user,
    role: state.auth.auth.details.role,
  };
};

/**
 * Maps Redux dispatch functions to component props
 * @param {Function} dispatch - Redux dispatch function
 * @returns {Object} Empty object (no actions to dispatch)
 */
const mapDispatchToProps = (dispatch) => {
  return {};
};

export default connect(mapStateToProps, mapDispatchToProps)(ViewEarnings);
